import { WEEKDAYS } from '../utils/bookingUtils';

/**
 * Flips one entry in a list that renders "empty" as "all", returning the
 * explicit result. Ticking everything collapses back to [] so the stored
 * value keeps meaning "all" the one way the rest of the app reads it
 * (Firebase can't store an empty list, so empty has to be the universal one).
 *
 * @param {number[]} shown - what is currently ticked
 * @param {number} value
 * @param {number} total - how many options exist
 * @returns {number[]}
 */
const toggleIn = (shown, value, total) => {
  const next = shown.includes(value) ? shown.filter(v => v !== value) : [...shown, value];
  return next.length === total ? [] : next.sort((a, b) => a - b);
};

/**
 * Weekday chips and court checkboxes for a setting that can be limited to
 * some days and courts — the minimum booking hours rule and each block
 * booking in Config -> Booking Settings.
 *
 * Nothing ticked is stored as [] and means every day / every court, so it
 * is rendered all-ticked.
 *
 * @param {object} props
 * @param {number[]} [props.days] - 0-6 (Sun-Sat); [] = every day
 * @param {number[]} [props.courts] - court numbers; [] = every court
 * @param {Array<{number: number, label: string}>} props.courtOptions - every configured court
 * @param {(patch: {days?: number[], courts?: number[]}) => void} props.onChange
 * @returns {JSX.Element}
 */
function DayCourtScopePicker({ days = [], courts = [], courtOptions, onChange }) {
  const dayList = (Array.isArray(days) ? days : []).map(Number);
  const courtList = (Array.isArray(courts) ? courts : []).map(Number);
  const shownDays = dayList.length > 0 ? dayList : WEEKDAYS.map(d => d.index);
  const shownCourts = courtList.length > 0 ? courtList : courtOptions.map(c => c.number);

  return (
    <>
      <div className="bks-picker">
        <span className="bks-picker-label">Days</span>
        <div className="bks-chips">
          {WEEKDAYS.map(({ index, short, label }) => (
            <button
              key={index}
              type="button"
              title={label}
              className={`bks-chip${shownDays.includes(index) ? ' on' : ''}`}
              onClick={() => onChange({ days: toggleIn(shownDays, index, WEEKDAYS.length) })}
            >
              {short}
            </button>
          ))}
        </div>
      </div>

      {courtOptions.length > 0 && (
        <div className="bks-picker">
          <span className="bks-picker-label">Courts</span>
          <div className="bks-courts">
            {courtOptions.map(court => (
              <label key={court.number} className="bks-court">
                <input
                  type="checkbox"
                  checked={shownCourts.includes(court.number)}
                  onChange={() => onChange({ courts: toggleIn(shownCourts, court.number, courtOptions.length) })}
                />
                {court.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default DayCourtScopePicker;
